import axios from "axios";
import * as Analytics from "./Analytics";
import {SessionContext} from "./SessionContext";

// A 401 THAT MEANS "THAT PASSWORD IS WRONG" IS NOT AN EXPIRED SESSION, and must not be replayed.
//
// The handler below refreshes the token and retries on every 401. For a re-authentication refusal
// that retry submits the same wrong password a second time, and each attempt counts against the
// account's five-strike lockout -- so one click costs two strikes and a person gets two and a half
// tries before they are locked out for an hour. Measured in prod: one Record click left the
// counter at 2.
//
// A DENY LIST, NOT AN ALLOW LIST. An unknown 401 still refreshes and replays exactly as before, so
// session renewal cannot regress on a code nobody thought of. These four are the ones that mean
// the credential just typed was wrong, missing, or already locked out.
const REAUTHENTICATION_REFUSALS = ["CC-OAPR-PWBD", "CC-OAPR-PWMS", "CC-OAPR-PWNO", "CC-OAPR-PWLK"];

function isAReauthenticationRefusal(response: any): boolean {
    const code = response?.data?.Code;
    return typeof code === "string" && REAUTHENTICATION_REFUSALS.includes(code);
}

export function makeCresAxiosRestAPI(sessionContext: any) {

    if (!sessionContext) sessionContext = new SessionContext();

    const instance = axios.create({
        baseURL: process.env.REACT_APP_REST_API_URL,
        // metadata: {},
    });

    instance.interceptors.request.use(
        (config: any) => {

            if (config.metadata === undefined) {
                config.metadata = {};
                config.metadata.startTime = null;
                config.metadata.endTime = null;
            }
            config.metadata.startTime = new Date();

            if (config.url !== "t3/v1/log") {
                Analytics.record("API Request", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                });
            }

           const token = sessionContext.getAccessToken();
            if (token) config.headers["X-Cres-AccessToken"] = token;

            // THE ACTIVE PERSONA TRAVELS WITH EVERY REQUEST. Same block, same reasoning as
            // AxiosFoundationAPI, which states it at length: the server scopes a read by the
            // persona these two headers name, and both t1's and t3's dispatchers now require one
            // on every door that does not explicitly opt out.
            //
            // THIS INSTANCE REACHES t1 DOORS, which is easy to miss because it is the REST client.
            // Attaching a file PUTs t1/v1/project_plan_phases and t1/v1/project_plan_phase_milestones
            // through here with only the two idoc headers, and DocumentRevisionRD sends
            // `this.getVisibilityMode()`, which is null until a PlatformTable sets it. All three
            // were refused CC-PRSN-NONE at 400.
            //
            // ONLY WHEN NOT ALREADY SET, and null and empty count as unset -- a component querying
            // under a scope the page chose has made a decision this fills gaps around.
            const active = sessionContext.getActiveVisibility();
            const requested = config.headers["visibility-mode"];
            if (active?.Mode && (requested === undefined || requested === null || requested === "")) {
                config.headers["visibility-mode"] = active.Mode;
                config.headers["visibility-value"] = active.Value ?? "";
            }

            return config;
        },
        (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
        (response: any) => {

            // Sometime response.data is string instead of object #1723
            // https://github.com/axios/axios/issues/1723
            if (response.headers["content-type"]?.includes("application/json")) {
                if (typeof response.data === "string" && response.data !== "") {
                    try {
                        response.data = JSON.parse(
                            response.data
                                .replace(/\\/g, "\\\\")
                                .replace(/\n/g, "\\n")
                                .replace(/\r/g, "\\r")
                                .replace(/\\"/g, "\""),
                        );
                    } catch (e) {
                        console.error(e);
                    }
                }
            }

            const config = response.config;

            config.metadata.endTime = new Date();

            if (config.url !== "t3/v1/log") {
                Analytics.record("API Response", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                    "HTTP Code": response.status,
                    "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                    "Reason Code": response.data ? response.data.Code : undefined,
                });
            }

            return response;
        },
        (error) => {

            const config = error.config;
            const response = error.response;

            config.metadata.endTime = new Date();

            // response can be undefined when there is a network issue / no connection
            if (response === undefined) {
                return Promise.reject(error);
            }

            if (config.url !== "t1/v1/log") {
                Analytics.record("API Response", {
                    "HTTP URL": config.url,
                    "HTTP Method": config.method,
                    "HTTP Code": response.status,
                    "HTTP Duration": (config.metadata.endTime - config.metadata.startTime) / 1000,
                    "Reason Code": response.data ? response.data.Code : undefined,
                });
            }

            if (config.metadata.disableRefresh !== true) {
                if (config.metadata.cogntioRefreshed === undefined
                    && !isAReauthenticationRefusal(response)) {

                    config.metadata.cogntioRefreshed = true;

                    if (response.status === 401) {
                        return axios({
                            method: "PUT",
                            url: `${process.env.REACT_APP_API_URL}/t1/v1/authenticate`,
                            data: {
                                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
                            },
                        }).then((response) => {
                            const token = response.data.AccessToken;
                            sessionContext.setAccessToken(token);
                            config.headers["X-Cres-AccessToken"] = token;
                            // Retry through `instance`, NOT raw axios: the response interceptor must run on the retry
                            // too, or its JSON-string repair is skipped and callers silently receive an unparsed body.
                            // No loop risk: cogntioRefreshed is already set, so a second 401 falls through to reject.
                            return instance(config);
                        });
                    }
                }
            }

            return Promise.reject(error);
        },
    );

    return instance;
}

const AxiosRestAPI = makeCresAxiosRestAPI(null);

export default AxiosRestAPI;
